import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import starIcon from '@/assets/svg/static/star.svg';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import { updatedImageUrl } from '@/utils/formatting';
import { isVideoUrl } from '@/utils/mediaUtils';
import {
  formatRatingStr,
  getRatingColor,
  getRatingEmoji,
} from '@/utils/rating';
import {
  BASE_REFERENCE_HEIGHT,
  BASE_REFERENCE_WIDTH,
  HALF_HEIGHT,
  IMAGE_BORDER_RADIUS,
  IMAGE_INSET_MULTIPLIER,
  MAX_HEIGHT_ORIGINALS,
  MIN_SWIPE_DISTANCE,
} from './CarouselV2.constants';
import styles from './CarouselV2.module.css';
import type { CarouselV2Props } from './CarouselV2.types';
import { useWindowDimensions } from './hooks/useWindowDimensions';

export const CarouselV2 = React.memo(
  ({
    productDetails,
    allImages,
    storeRating,
    onTryOnClick,
    onImageClick,
    videoUris,
  }: CarouselV2Props): JSX.Element => {
    const { width: containerWidth, height: containerHeight } =
      useWindowDimensions();
    const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    const isOriginal = productDetails.isSlaydOriginals ?? false;
    const hasVideos = videoUris && videoUris.length > 0;

    const [touchStart, setTouchStart] = useState<number>(0);
    const [touchEnd, setTouchEnd] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [translateX, setTranslateX] = useState<number>(0);
    const isMovedRef = useRef<boolean>(false);

    const [naturalDims, setNaturalDims] = useState<{
      width: number;
      height: number;
    } | null>(null);

    useEffect(() => {
      if (allImages && allImages.length > 0) {
        const img = new window.Image();
        img.src = updatedImageUrl(allImages[0]);
        img.onload = () => {
          setNaturalDims({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        };
      }
    }, [allImages]);

    const imageDimensions = useMemo(() => {
      if (naturalDims && naturalDims.width > 0 && naturalDims.height > 0) {
        const aspectRatio = naturalDims.width / naturalDims.height;
        const maxHeight = containerHeight * MAX_HEIGHT_ORIGINALS;
        const srcWidth = containerHeight;
        const calculatedHeight = Math.min(srcWidth / aspectRatio, maxHeight);
        return {
          width: containerWidth,
          height: calculatedHeight,
        };
      }
      return {
        width: containerWidth,
        height: containerHeight * HALF_HEIGHT,
      };
    }, [naturalDims, containerWidth, containerHeight]);

    const layoutDimensions = useMemo(() => {
      if (!isOriginal) {
        return {
          width: imageDimensions.width,
          height: imageDimensions.height,
        };
      }
      const widthRef = containerWidth / BASE_REFERENCE_WIDTH;
      const heightRef = containerHeight / BASE_REFERENCE_HEIGHT;
      return {
        width: imageDimensions.width - IMAGE_INSET_MULTIPLIER * widthRef,
        height: imageDimensions.height - IMAGE_INSET_MULTIPLIER * heightRef,
      };
    }, [isOriginal, imageDimensions, containerWidth, containerHeight]);

    const imageStyle = useMemo((): React.CSSProperties => {
      return {
        width: `${layoutDimensions.width}px`,
        height: `${layoutDimensions.height}px`,
        borderRadius: `${IMAGE_BORDER_RADIUS}px`,
        objectFit: 'cover',
      };
    }, [layoutDimensions]);

    const updateIndex = useCallback(
      (newIndex: number): void => {
        trackEvent(EVENTS.PDP_V2_CAROUSEL_SWIPE, {
          [PROPERTIES.PRODUCT_ID]: productDetails.id,
          [PROPERTIES.FROM_INDEX]: currentIndex,
          [PROPERTIES.TO_INDEX]: newIndex,
          [PROPERTIES.TOTAL_SLIDES]: allImages.length,
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });
        setCurrentIndex(newIndex);
      },
      [productDetails.id, currentIndex, allImages.length]
    );

    const handleImageClick = useCallback(
      (index: number): void => {
        if (!isMovedRef.current) {
          trackEvent(EVENTS.PDP_V2_CAROUSEL_IMAGE_CLICK, {
            [PROPERTIES.PRODUCT_ID]: productDetails.id,
            [PROPERTIES.IMAGE_INDEX]: index,
            [PROPERTIES.IMAGE_URL]: allImages[index],
            [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
          });
          onImageClick(index);
        }
      },
      [productDetails.id, allImages, onImageClick]
    );

    const handleTouchStart = (e: React.TouchEvent): void => {
      if (allImages.length <= 1) return;
      setTouchEnd(0);
      setTouchStart(e.targetTouches[0].clientX);
      setIsDragging(true);
      setTranslateX(0);
      isMovedRef.current = false;
    };

    const handleTouchMove = (e: React.TouchEvent): void => {
      if (!isDragging || allImages.length <= 1) return;
      const currentTouch = e.targetTouches[0].clientX;
      const diff = touchStart - currentTouch;

      if (Math.abs(diff) > 5) {
        isMovedRef.current = true;
      }

      const isFirstImage = currentIndex === 0;
      const isLastImage = currentIndex === allImages.length - 1;
      if ((isFirstImage && diff < 0) || (isLastImage && diff > 0)) return;

      setTranslateX(-diff);
      setTouchEnd(currentTouch);
    };

    const handleTouchEnd = (): void => {
      if (!touchStart || !touchEnd || allImages.length <= 1) {
        setIsDragging(false);
        setTranslateX(0);
        return;
      }

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
      const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

      if (isLeftSwipe && currentIndex < allImages.length - 1) {
        updateIndex(currentIndex + 1);
      }
      if (isRightSwipe && currentIndex > 0) {
        updateIndex(currentIndex - 1);
      }

      setIsDragging(false);
      setTranslateX(0);
    };

    const handleMouseDown = (e: React.MouseEvent): void => {
      if (allImages.length <= 1) return;
      setTouchEnd(0);
      setTouchStart(e.clientX);
      setIsDragging(true);
      setTranslateX(0);
      isMovedRef.current = false;
    };

    const handleMouseMove = (e: React.MouseEvent): void => {
      if (!isDragging || allImages.length <= 1) return;
      const diff = touchStart - e.clientX;

      if (Math.abs(diff) > 5) {
        isMovedRef.current = true;
      }

      const isFirstImage = currentIndex === 0;
      const isLastImage = currentIndex === allImages.length - 1;
      if ((isFirstImage && diff < 0) || (isLastImage && diff > 0)) return;

      setTranslateX(-diff);
      setTouchEnd(e.clientX);
    };

    const handleMouseUp = (): void => {
      if (!touchStart || !touchEnd || allImages.length <= 1) {
        setIsDragging(false);
        setTranslateX(0);
        return;
      }

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
      const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

      if (isLeftSwipe && currentIndex < allImages.length - 1) {
        updateIndex(currentIndex + 1);
      }
      if (isRightSwipe && currentIndex > 0) {
        updateIndex(currentIndex - 1);
      }

      setIsDragging(false);
      setTranslateX(0);
    };

    return (
      <div
        className={`${styles.carouselFrame} ${isOriginal ? styles.originalsCarouselFrame : ''}`}
        style={
          isOriginal
            ? {
                width: `${layoutDimensions.width}px`,
              }
            : undefined
        }
      >
        <div
          className={`${styles.productImage} ${isOriginal ? styles.productImageOriginal : ''}`}
        >
          <div
            className={styles.carouselContainer}
            style={{
              width: `${layoutDimensions.width}px`,
              height: imageStyle.height,
              borderRadius: `${IMAGE_BORDER_RADIUS}px`,
            }}
          >
            {!naturalDims && allImages && allImages.length > 0 && (
              <div className={styles.carouselShimmer} />
            )}
            <div
              className={`${styles.carouselWrapper} ${
                isDragging ? styles.grabbing : styles.grab
              }`}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div
                className={`${styles.carouselTrack} ${isDragging ? styles.noTransition : ''}`}
                style={{
                  width: `${allImages.length * layoutDimensions.width}px`,
                  transform: `translateX(${-currentIndex * layoutDimensions.width + translateX}px)`,
                }}
              >
                {allImages.map((imageUrl, index) => (
                  <div
                    key={index}
                    className={styles.carouselSlide}
                    style={{ width: `${layoutDimensions.width}px` }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={updatedImageUrl(imageUrl)}
                      alt={`${productDetails.name} ${index + 1}`}
                      style={imageStyle}
                      draggable={false}
                    />
                  </div>
                ))}
              </div>
            </div>

            {allImages.length > 1 && (
              <div
                className={`${styles.carouselDots} ${
                  isOriginal ? styles.carouselDotsOriginal : ''
                }`}
              >
                {allImages.map((_, index) => (
                  <div
                    key={index}
                    className={`${styles.dot} ${
                      isOriginal && index !== currentIndex
                        ? styles.dotOriginalInactive
                        : ''
                    } ${index === currentIndex ? styles.activeDot : ''}`}
                  />
                ))}
              </div>
            )}
          </div>

          {hasVideos &&
            (() => {
              const firstUri = videoUris[0];
              const isVideo = isVideoUrl(firstUri);
              return (
                <div
                  className={`${styles.videoTryOnThumbnail} ${
                    isOriginal ? styles.videoTryOnThumbnailOriginal : ''
                  }`}
                  onClick={onTryOnClick}
                >
                  {!isVideoLoaded && <div className={styles.videoSkeleton} />}
                  {isVideo ? (
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className={`${styles.thumbnailVideo} ${
                        isVideoLoaded ? styles.loaded : ''
                      }`}
                      src={updatedImageUrl(firstUri)}
                      onLoadedData={() => setIsVideoLoaded(true)}
                    />
                  ) : (
                    <img
                      className={`${styles.thumbnailVideo} ${
                        isVideoLoaded ? styles.loaded : ''
                      }`}
                      src={updatedImageUrl(firstUri)}
                      alt="Influencer pick preview"
                      onLoad={() => setIsVideoLoaded(true)}
                    />
                  )}
                </div>
              );
            })()}

          {(() => {
            const hasProductRating = (productDetails.rating ?? 0) > 0;
            const hasStoreRating =
              storeRating != null && parseFloat(storeRating) > 0;

            if (hasProductRating) {
              const bgCol = getRatingColor(productDetails.rating);
              return (
                <div
                  className={`${styles.ratingContainer} ${isOriginal ? styles.ratingContainerOriginal : ''}`}
                  style={{ backgroundColor: bgCol }}
                >
                  <div className={styles.ratingRow}>
                    <span className={styles.ratingNumber}>
                      {formatRatingStr(productDetails.rating)}
                    </span>
                    <img src={starIcon} alt="" className={styles.ratingStar} />
                  </div>
                  {productDetails.ratingCount != null && (
                    <span className={styles.ratingCount}>
                      {productDetails.ratingCount} ratings
                    </span>
                  )}
                </div>
              );
            } else if (hasStoreRating) {
              const borderCol = getRatingColor(storeRating);
              const emoji = getRatingEmoji(storeRating);
              return (
                <div
                  className={`${styles.storePolicyContainer} ${isOriginal ? styles.ratingContainerOriginal : ''}`}
                  style={{ borderColor: borderCol }}
                >
                  <div className={styles.storePolicyRow}>
                    {emoji && (
                      <img src={emoji} alt="" className={styles.storeEmoji} />
                    )}
                    <span
                      className={styles.storePolicyNumber}
                      style={{ color: borderCol }}
                    >
                      {formatRatingStr(storeRating)}
                    </span>
                  </div>
                  <span className={styles.storePolicyText}>Store Policy</span>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>
    );
  }
);

export default CarouselV2;
